import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { PictureAsset } from "@/components/site/PictureAsset";
import "@/components/site/Studio.css";

const PORTRAIT_BASE = "/assets/homepage/studio/portrait";

export default function Studio({ id = "studio" }) {
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef(null);

  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;
    const targets = root.querySelectorAll("[data-studio-reveal]");
    if (reducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach((n) => n.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [reducedMotion]);

  return (
    <section
      ref={sectionRef}
      id={id}
      className="studio"
      data-testid="studio-section"
      aria-label="The Studio"
    >
      <div className="studio__inner">
        <div className="studio__text">
          <span
            className="studio__eyebrow"
            data-studio-reveal
            data-testid="studio-eyebrow"
          >
            The Studio
          </span>

          <h2
            className="studio__heading"
            data-studio-reveal
            data-testid="studio-heading"
            style={{ transitionDelay: "120ms" }}
          >
            Designed Around People
          </h2>

          <p
            className="studio__body"
            data-studio-reveal
            data-testid="studio-body"
            style={{ transitionDelay: "260ms" }}
          >
            After six years of practicing and founding a design studio,
            Ōraṇi emerged as a natural evolution for Shvetha - a
            movement towards a more conscious, human-centric and
            experiential way of thinking about design. It grew from a
            desire to look beyond the surface of a space and understand
            the people, stories and experiences that give it meaning.
            Ōraṇi is an evolving practice shaped by this curiosity,
            exploring how design can create spaces that are thoughtful,
            expressive and deeply connected to the lives unfolding
            within them.
          </p>
        </div>

        <figure
          className="studio__figure"
          data-studio-reveal
          data-testid="studio-figure"
          style={{ transitionDelay: "180ms" }}
        >
          <div className="studio__image-wrap">
            <PictureAsset
              base={PORTRAIT_BASE}
              alt="Shvetha, founder and principal architect of Ōraṇi, seated before a framed floral painting"
              className="studio__image"
              sizes="(min-width: 880px) 45vw, 100vw"
            />
          </div>
          <figcaption
            className="studio__caption"
            data-testid="studio-caption"
          >
            Shvetha, Founder & Principal Architect.
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
